import React from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

/**
 * Compatibility helpers for react-router v7.
 *
 * The code base was written against react-router v5 (`withRouter`, `history.push`,
 * `match.params`). These helpers expose the same shape on top of the v7 hooks so
 * that connected components keep receiving `history`, `location` and `match` props.
 */
export function useHistory() {
  const navigate = useNavigate();
  return React.useMemo(
    () => ({
      push: (to, state) => navigate(to, { state }),
      replace: (to, state) => navigate(to, { replace: true, state }),
      goBack: () => navigate(-1),
      go: (n) => navigate(n),
    }),
    [navigate]
  );
}

export function withRouter(Component) {
  function ComponentWithRouterProps(props) {
    const location = useLocation();
    const params = useParams();
    const history = useHistory();
    const match = React.useMemo(
      () => ({ params, path: location.pathname, url: location.pathname }),
      [params, location.pathname]
    );
    return <Component {...props} history={history} location={location} match={match} />;
  }
  ComponentWithRouterProps.displayName = `withRouter(${Component.displayName || Component.name || "Component"})`;
  return ComponentWithRouterProps;
}
